import { create } from 'zustand';

interface NodeAppearanceState {
  showLatency: boolean;
  showTokens: boolean;
  showTags: boolean;
  setShowLatency: (show: boolean) => void;
  setShowTokens: (show: boolean) => void;
  setShowTags: (show: boolean) => void;
}

// Helper functions for localStorage
const STORAGE_KEYS = {
  SHOW_LATENCY: 'ls:run-tree-show-latency',
  SHOW_TOKENS: 'ls:run-tree-show-tokens',
  SHOW_TAGS: 'ls:run-tree-show-tags',
} as const;

const getStoredValue = (key: string, defaultValue: boolean): boolean => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const setStoredValue = (key: string, value: boolean): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Silently fail if localStorage is not available
  }
};

export const useNodeAppearanceStore = create<NodeAppearanceState>((set) => ({
  showLatency: getStoredValue(STORAGE_KEYS.SHOW_LATENCY, true),
  showTokens: getStoredValue(STORAGE_KEYS.SHOW_TOKENS, true),
  showTags: getStoredValue(STORAGE_KEYS.SHOW_TAGS, false),

  setShowLatency: (show: boolean) => {
    setStoredValue(STORAGE_KEYS.SHOW_LATENCY, show);
    set({ showLatency: show });
  },

  setShowTokens: (show: boolean) => {
    setStoredValue(STORAGE_KEYS.SHOW_TOKENS, show);
    set({ showTokens: show });
  },

  setShowTags: (show: boolean) => {
    setStoredValue(STORAGE_KEYS.SHOW_TAGS, show);
    set({ showTags: show });
  },
}));

// Convenience hook to access just the values and setters
export const useNodeAppearanceSettings = (isPublicPage: boolean) => {
  const {
    showLatency,
    showTokens,
    showTags: _showTags,
    setShowLatency,
    setShowTokens,
    setShowTags,
  } = useNodeAppearanceStore();

  const showTags = isPublicPage ? false : _showTags;

  return {
    showLatency,
    showTokens,
    showTags,
    setShowLatency,
    setShowTokens,
    setShowTags,
  };
};
